import type { ScanComparison } from '../../lib/dashboard/scanComparison';
import { Eyebrow } from '../concept/eyebrow';
import {
  formatBodyFat,
  formatChange,
  formatCm,
  formatWeight,
  metricChange,
} from '../lib/metric-format';

const shortDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** A change between the two columns, by the dashboard's one rule per metric. */
const change = (key: string, a: number, b: number) => formatChange(key, metricChange(key, a, b));

const Row: React.FC<{ label: string; earlier: string; later: string; delta?: string }> = ({
  label,
  earlier,
  later,
  delta,
}) => (
  <>
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="tabular text-xs">{earlier}</span>
    <span className="tabular text-xs font-semibold">{later}</span>
    <span className="tabular text-xs font-semibold text-primary">{delta ?? ''}</span>
  </>
);

/**
 * Two chosen scans, side by side.
 *
 * Deliberately the same rows in the same order as the Then / Now hero: a coach
 * comparing any two scans is asking the question the hero already answers for
 * first-and-latest, so it should not be a different vocabulary.
 */
export const ScanComparisonSheet: React.FC<{
  /** Null when the two chosen scans cannot be compared. */
  comparison: ScanComparison | null;
  onClose: () => void;
  /** Whether the muscle-mass estimate is shown in this experience (Config.showMuscleMass). */
  showMuscle?: boolean;
}> = ({ comparison, onClose, showMuscle = false }) => {
  const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/30">
      {/* Backdrop dismiss, so the sheet behaves like a sheet. */}
      <button
        type="button"
        aria-label="Close comparison"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />

      <div
        data-testid="scan-comparison"
        className="relative w-full max-w-2xl rounded-t-lg border border-border bg-card p-5 pb-8 shadow-lg"
      >
        <div className="mb-4 flex items-baseline justify-between">
          <Eyebrow>Comparing two scans</Eyebrow>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] font-medium text-primary"
          >
            Done
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  if (!comparison) {
    // Two scans were chosen and neither can be placed beside the other. Saying
    // so beats a tap that does nothing: the usual cause is a scan captured
    // without a weight, which leaves body composition underivable.
    return (
      <Shell>
        <p className="text-xs leading-relaxed text-muted-foreground">
          These two scans cannot be compared. Body composition needs both a
          weight and a body-fat reading, and at least one of them is missing a
          weight.
        </p>
      </Shell>
    );
  }

  const { earlier, later } = comparison;

  return (
    <Shell>
      <div className="grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-x-4 gap-y-2.5">
          <span />
          <span className="font-mono text-[8.5px] uppercase tracking-[0.08em] text-muted-foreground">
            {shortDate(earlier.date)}
          </span>
          <span className="font-mono text-[8.5px] uppercase tracking-[0.08em] text-primary">
            {shortDate(later.date)}
          </span>
          <span className="font-mono text-[8.5px] uppercase tracking-[0.08em] text-muted-foreground">
            Change
          </span>

          <Row
            label="Weight"
            earlier={formatWeight(earlier.weightKg)}
            later={formatWeight(later.weightKg)}
            delta={change('weight', earlier.weightKg, later.weightKg)}
          />
          <Row
            label="Body fat"
            earlier={formatBodyFat(earlier.fatPercent)}
            later={formatBodyFat(later.fatPercent)}
            delta={change('body_fat', earlier.fatPercent, later.fatPercent)}
          />
          <Row
            label="Fat mass"
            earlier={formatWeight(earlier.fatKg)}
            later={formatWeight(later.fatKg)}
            delta={change('weight', earlier.fatKg, later.fatKg)}
          />
          {earlier.waistCm !== undefined && later.waistCm !== undefined && (
            <Row
              label="Waist"
              earlier={formatCm(earlier.waistCm)}
              later={formatCm(later.waistCm)}
              delta={change('waist', earlier.waistCm, later.waistCm)}
            />
          )}
        <Row
          label="Lean"
          earlier={formatWeight(earlier.leanKg)}
          later={formatWeight(later.leanKg)}
          delta={change('weight', earlier.leanKg, later.leanKg)}
        />
      </div>

      <p className="mt-4 text-[10.5px] leading-relaxed text-muted-foreground">
        Both columns are measured scans. Fat from the tape body-fat estimate
        {showMuscle ? ' · muscle via Lee 2000 from lean mass.' : '; lean is the rest of the weight.'}
      </p>
    </Shell>
  );
};
